import CustomTable from "components/common/CustomTable";
import Dropdown from "components/common/Dropdown";
import MultiChannelSelect from "components/common/MultiChannelSelect";
import SynthTooltip from "components/common/SynthTooltip";
import { formatMoney } from "helpers";
import useGetSalesChannel from "hooks/Dashboard/useGetSalesChannel";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import ResponsivePaginationComponent from "react-responsive-pagination";
import { dashboardActions } from "redux/Ldb/actions";
import CustomCalendar from "components/common/CalendarComp.js/Calendar";
import Button from "components/common/Button";

type ComponentForecastProps = {
    resultMessage: {
        filters: Record<string, any>;
        inventories: Array<Record<string, any>>;
        pagination: {
            current: number;
            next: number;
            number_of_pages: number;
            perPage: number;
        };
        total: number;
        summary: string;
    };
    toolName: string;
};
const ComponentForecast = ({ resultMessage, toolName }: ComponentForecastProps) => {
    const customRef = useRef(null);

    const dispatch = useAppDispatch();
    const [expandedRowIdx, setExpandedRowIdx] = useState<number>(-1);
    const [page, setPage] = useState<{
        currentPage: number;
        noOfPages: number;
    }>({
        currentPage: 1,
        noOfPages: 1,
    });

    const [inventoriesList, setInventoriesList] = useState<any[]>([]);
    const [channelFilter, setChannelFilter] = useState<string>("");
    const [selectedPeriod, setSelectedPeriod] = useState<string>("");
    const [displayPeriod, setDisplayPeriod] = useState<string>("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [channel, setChannel] = useState<string>("");
    const [diff, setDiff] = useState<number>(0);
    const [options, setOptions] = useState<any[]>([]);
    const [showCustomDate, setShowCustomDate] = useState<boolean>(false);
    const [customValue, customOnChange] = useState("");
    const [date, setDate] = useState<{ startDate: string; endDate: string }>({
        startDate: "",
        endDate: "",
    });

    const {
        fetchingNonFinishedGoodsForecast,
        fetchNonFinishedGoodsForecastSuccess,
        fetchNonFinishedGoodsForecastFailure,
    } = useAppSelector((state) => state.dashboard);
    const { data: salesChannel, isFetching: salesChannelLoading } = useGetSalesChannel();

    const salesChannelOption = salesChannel
        ?.filter(
            (channel) =>
                channel?.toLowerCase() === "shopify" ||
                channel?.toLowerCase() === "retail" ||
                channel?.toLowerCase() === "amazon"
        )
        ?.map((channel) => {
            return {
                label: channel,
                value: channel,
                icon:
                    channel?.toLowerCase() === "shopify" ? (
                        <img
                            src="https://storage.googleapis.com/heysynth_files/synth_webapp/shopify_w87tn3.svg"
                            alt="shopify"
                            className="w-4 h-4"
                        />
                    ) : channel?.toLowerCase() === "amazon" ? (
                        <img
                            src="https://storage.googleapis.com/heysynth_files/synth_webapp/amazon_1_aeftdf.svg"
                            alt="amazon"
                            className="w-4 h-4"
                        />
                    ) : (
                        <i className="ri-store-2-fill text-y-10 text-base"></i>
                    ),
            };
        });

    const tableData =
        page?.currentPage === 1 && !channel && !startDate && !endDate
            ? resultMessage?.inventories
            : inventoriesList;

    useEffect(() => {
        if ((page?.currentPage !== 1 && channel) || channel || startDate || endDate) {
            dispatch(
                dashboardActions?.getNonFinishGoodsForecast(
                    page?.currentPage,
                    "",
                    resultMessage?.filters?.inventoryId ?? "",
                    channel ?? resultMessage?.filters?.channels?.join(","),
                    "",
                    "",
                    startDate ?? resultMessage?.filters?.startDate,
                    endDate ?? resultMessage?.filters?.endDate
                )
            );
        }
    }, [dispatch, page?.currentPage, channel, startDate, endDate]);

    useEffect(() => {
        if (Boolean(fetchNonFinishedGoodsForecastSuccess)) {
            setInventoriesList(fetchNonFinishedGoodsForecastSuccess?.inventories);
        }
    }, [fetchNonFinishedGoodsForecastSuccess]);

    useEffect(() => {
        if (resultMessage?.filters?.startDate && resultMessage?.filters?.endDate) {
            setDiff(
                moment(resultMessage?.filters?.endDate).diff(
                    moment(resultMessage?.filters?.startDate),
                    "months",
                    true
                )
            );
        }
    }, [resultMessage?.filters?.startDate, resultMessage?.filters?.endDate, diff]);

    useEffect(() => {
        const monthDiff = Math.ceil(diff);
        if (monthDiff) {
            const opt = [];
            for (let i = 0; i < monthDiff; i++) {
                opt.push(i + 1);
            }

            setOptions(opt);
        }
    }, [diff]);

    useEffect(() => {
        if (
            (resultMessage?.filters?.startDate && resultMessage?.filters?.endDate) ||
            (startDate && endDate)
        ) {
            setDate({
                startDate: startDate || resultMessage?.filters?.startDate,
                endDate: endDate || resultMessage?.filters?.endDate,
            });
        }
    }, [resultMessage?.filters?.startDate, resultMessage?.filters?.endDate, startDate, endDate]);

    useEffect(() => {
        if (customValue[0] !== customValue[1]) {
            setStartDate(moment(customValue[0])?.format("YYYY-MM-DD"));
            setEndDate(moment(customValue[1])?.format("YYYY-MM-DD"));
        }
    }, [customValue]);

    const componentTableHeader = [
        {
            title: null,
            widthClass: "w-[50px] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">Component</span>

                    <SynthTooltip
                        content={
                            "Individual materials or ingredients used to produce finished goods. Components are tracked separately to ensure accurate forecasting and procurement."
                        }
                        divId={"component-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[150px] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Current stock level
                    </span>

                    <SynthTooltip
                        content={
                            "The actual quantity of the product currently available in stock for sale or use at this moment."
                        }
                        divId={"current-stock-level-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[180px] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Forecasted qty in use
                    </span>

                    <SynthTooltip
                        content={
                            "The estimated amount of this component expected to be consumed in the production of finished goods, based on current demand forecasts and planned manufacturing activity."
                        }
                        divId={"forecasted-qty-in-use-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[200px] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Forecasted needs
                    </span>

                    <SynthTooltip
                        content={
                            "The estimated amount of the product required to meet anticipated demand during the forecast period."
                        }
                        divId={"forecasted-needs-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[160px] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Stock deficit
                    </span>

                    <SynthTooltip
                        content={
                            "The number of units your inventory is short based on forecasted demand. This highlights how much stock needs to be replenished to meet future needs."
                        }
                        divId={"stock-deficit-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[150px] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Forecasted cost
                    </span>

                    <SynthTooltip
                        content={
                            "The predicted total cost to fulfill the forecasted demand, based on the product's unit cost and the stock deficit."
                        }
                        divId={"forecasted-cost-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[150px] !border-r-none",
        },
    ];

    const componentTableBody = tableData?.map((item, idx) => {
        const stockDeficit =
            item?.productStockDetails?.stockLevel - item?.forecast?.forecastedNeeds;
        const isPinned = item?.isPinned;

        return [
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 flex justify-center items-center text-xl text-slate-600  py-2`}
                    >
                        <i
                            className={`ri-star-fill text-center cursor-default ${
                                isPinned ? "text-[#FCD34D]" : "text-[#E2E8F0] "
                            }`}
                            
                        ></i>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs break-all ">{item?.sku}</p>

                            <p className="">{item?.productName}</p>
                        </div>
                    </div>
                ),
                expandedRowAction: () => {
                    idx === expandedRowIdx ? setExpandedRowIdx(-1) : setExpandedRowIdx(idx);
                },
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                {formatMoney()?.format(item?.productStockDetails?.stockLevel)}{" "}
                                {item?.productStockDetails?.unitOfMeasurement || ""}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                {formatMoney()?.format(item?.forecast?.forecast)}{" "}
                                {item?.productStockDetails?.unitOfMeasurement || ""}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p
                                className={`${
                                    item?.forecast?.forecastedNeeds
                                        ? "text-red-500"
                                        : "text-slate-500"
                                } text-xs `}
                            >
                                {item?.forecast?.forecastedNeeds === 0 ? (
                                    <span className="text-green-500">Sufficent</span>
                                ) : (
                                    formatMoney()?.format(item?.forecast?.forecastedNeeds) +
                                    " " +
                                    (item?.productStockDetails?.unitOfMeasurement || "")
                                )}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p
                                className={` text-xs ${
                                    String(stockDeficit)[0] === "-"
                                        ? "text-red-500"
                                        : "text-slate-500"
                                }`}
                            >
                                {String(stockDeficit)[0] === "-" ? (
                                    formatMoney()?.format(String(stockDeficit)?.slice(1)) +
                                    " " +
                                    (item?.productStockDetails?.unitOfMeasurement || "")
                                ) : (
                                    <span className="text-green-500">Sufficent</span>
                                )}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                ${formatMoney()?.format(item?.forecast?.forecastedCost)}
                            </p>
                        </div>
                    </div>
                ),
                widthClass: " !border-b",
            },
        ];
    });

    const expandedTableHeader = [
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Associated SKUs/products
                    </span>

                    <SynthTooltip
                        content={
                            "Finished goods (SKUs) that have this particular product as one of its components."
                        }
                        divId={"associated-product-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Forecasted qty
                    </span>

                    <SynthTooltip
                        content={
                            "The estimated number of units expected to be needed or used during the forecast period."
                        }
                        divId={"forecasted-qty-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
    ];

    const expandedTableBody = tableData?.[expandedRowIdx]?.inventoriesUsedIn?.map((item, idx) => {
        return [
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm  py-2`}
                    >
                        <div>
                            <p className=" text-sm ">{item?.inventory?.sku}</p>

                            <p className="text-sm text-slate-500">{item?.inventory?.productName}</p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm  py-2`}
                    >
                        <div>
                            <p className=" text-sm ">
                                {formatMoney()?.format(
                                    item?.estinmatedSalesForecast?.forecastedSale
                                )}{" "}
                                {resultMessage?.inventories?.[expandedRowIdx]?.productStockDetails
                                    ?.unitOfMeasurement || ""}
                            </p>
                        </div>
                    </div>
                ),
            },
        ];
    });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (customRef.current && !customRef.current.contains(event.target)) {
                setShowCustomDate(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [customRef]);
    return (
        <div className="relative border border-slate-200 rounded-xl py-[15px] mt-[16px]">
            <div className="flex justify-between px-5">
                <div className="flex items-center space-x-6 ">
                    <p className="text-g-75 font-rocGroteskMedium text-sm lg:text-lg mb-4 lg:mb-0">
                        Component Forecast
                    </p>
                </div>

                <div className="flex items-center space-x-4 ">
                    <div className="relative">
                        {/* <Dropdown
                            handleChange={(name, value) => {
                                setSelectedPeriod(
                                    typeof value === "string" && value?.includes("_days")
                                        ? value?.split("_")[0]
                                        : value
                                );
                                setEndDate(
                                    typeof value === "string" && value?.includes("_days")
                                        ? resultMessage?.filters?.endDate
                                        : moment(startDate)
                                              .add(value, "months")
                                              .format("YYYY-MM-DD")
                                );
                            }}
                            dropdown={
                                <div className="flex items-center justify-between h-[36px] rounded-[6px] sm:shadow-buttonShadow2 pt-[6px] sm:py-[6px] xs:px-[10px] xs:gap-2 border-0 sm:!border sm:!border-slate-100">
                                    <div
                                        className={`text-g-75 font-rocGroteskMedium text-[11px] xs:text-[13px] inline-block whitespace-nowrap w-fit ${
                                            channelFilter && "capitalize"
                                        }`}
                                    >
                                        {`${selectedPeriod || Math.ceil(diff)} months `}

                                        {`(${moment(startDate).format("MMM YYYY")} - ${moment(
                                            endDate
                                        ).format("MMM YYYY")})`}
                                    </div>
                                    <i className="ri-arrow-down-s-line text-xs xs:text-lg text-g-75"></i>
                                </div>
                            }
                            dropdownItemsClasses="!px-0 !py-0"
                            dropdownClassName="!mb-10 !-mt-7"
                            dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-[200px] !w-[190px] !min-w-min !left-0 !z-[1400]`}
                            name="valueRange"
                            dropdownOptions={options?.map((item, idx) => {
                                return {
                                    label: (
                                        <div
                                            className=" text-slate-600 font-rocGroteskMedium text-[13px] flex items-center gap-2
                      border-b border-b-slate-100 !w-[190px] px-4 py-[10px]"
                                        >{`${item} months`}</div>
                                    ),
                                    value:
                                        options?.length - 1 === idx && !Number.isInteger(diff)
                                            ? item + "_days"
                                            : item,
                                };
                            })}
                        /> */}
                        <Button
                            btnText={`${moment(date?.startDate).format("MMM, DD YYYY")} - ${moment(
                                date?.endDate
                            ).format("MMM, DD YYYY")}`}
                            btnClassname={
                                "!w-fit !py-2 flex items-center justify-between h-[36px] rounded-[6px] sm:shadow-buttonShadow2 pt-[6px] sm:py-[6px] xs:px-[10px] xs:gap-2 border-0 sm:!border sm:!border-slate-100 !bg-transparent "
                            }
                            onClick={() => setShowCustomDate(true)}
                        />
                        <div className="relative">
                            {showCustomDate && (
                                <div
                                    ref={customRef}
                                    className="w-[630px] z-[10000] absolute  right-0 top-0 px-[28px] pt-8 pb-8 bg-white
                                                    rounded-2xl shadow-[0px_0px_4px_rgba(0,0,0,0.04),0px_4px_32px_rgba(0,0,0,0.06)]"
                                >
                                    <CustomCalendar
                                        showDoubleView={true}
                                        selectRange={true}
                                        value={customValue}
                                        onChange={customOnChange}
                                        minDate={moment(resultMessage?.filters?.startDate).toDate()}
                                        maxDate={moment(resultMessage?.filters?.endDate).toDate()}
                                    />
                                    <div className="flex justify-end">
                                        <Button
                                            btnText={"Close"}
                                            btnClassname={"!w-fit !py-2"}
                                            onClick={() => setShowCustomDate(false)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex sm:space-x-2.5  max-sm:justify-between max-sm: items-end border-b border-b-slate-200 sm:border-b-0">
                        <MultiChannelSelect
                            value={channelFilter || resultMessage?.filters?.channels?.join(",")}
                            salesChannel={salesChannelOption?.map((item) => item.value)}
                            isLoading={salesChannelLoading}
                            onChange={(value) => {
                                setChannelFilter(value);
                                setChannel(value);
                            }}
                            dropdownContainerClasses=" !right-0"
                            dropdownClassName="!shadow-buttonShadow2 !h-auto !border-slate-100"
                        />
                    </div>
                </div>
            </div>

            <div className="mt-4 ">
                <div className="flex items-center justify-between py-[13px] border-t"></div>

                <div className="overflow-x-scroll px-5">
                    {toolName === "ViewComponentsForecast" &&
                        resultMessage?.inventories?.length > 0 && (
                            <>
                                <CustomTable
                                    tableBody={componentTableBody}
                                    tableHeader={componentTableHeader}
                                    isCellBordered={true}
                                    isCellSelectable={false}
                                    stickyHeader={true}
                                    headerContainerClass="!bg-slate-50 !border-slate-200"
                                    headerItemClass="font-rocGroteskMedium "
                                    tableClass="mb-12 !border-slate-200"
                                    isCollapseable={true}
                                    isCollapseableIconIndex={1}
                                    expandedRowIdx={expandedRowIdx}
                                    isScrollable={true}
                                    scrollWithin={true}
                                    rowExpanded={
                                        <div className="relative z-[1300]">
                                            <>
                                                <div className="pt-2.5 px-3 bg-slate-50">
                                                    {expandedTableBody?.length > 0 ? (
                                                        <div className="">
                                                            <CustomTable
                                                                tableBody={expandedTableBody}
                                                                tableHeader={expandedTableHeader}
                                                                isCellBordered={true}
                                                                isInifiniteScroll={false}
                                                                // isAllSelectable={true}
                                                                stickyHeader={true}
                                                                headerContainerClass="!bg-slate-50 !border-slate-200"
                                                                headerItemClass="font-rocGroteskMedium "
                                                                tableClass="my-3 !border-t-slate-200 !border-x-slate-200"
                                                                isScrollable={false}
                                                                tableContainerClass=""
                                                                // bodyItemClass="!border-slate-200"
                                                                expandedRowTClass="!z-[1300]"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="flex justify-center items-center h-full py-5">
                                                            <span className="text-center text-sm font-rocGroteskMedium">
                                                                No Associated Products available
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        </div>
                                    }
                                    renderEmptyTable={() => null}
                                />
                                <div className="flex justify-end mt-5">
                                    <ResponsivePaginationComponent
                                        current={page?.currentPage}
                                        total={resultMessage?.pagination?.number_of_pages}
                                        maxWidth={350}
                                        onPageChange={(page) => {
                                            setPage((prev) => ({
                                                ...prev,
                                                currentPage: page,
                                            }));
                                        }}
                                    />
                                </div>
                            </>
                        )}
                </div>
            </div>
        </div>
    );
};

export default ComponentForecast;
